import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

export interface PasswordFieldProps {
  /** Current password value */
  value: string;
  /** Change handler */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Keydown handler (e.g., for Enter key submission) */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Placeholder text (default: "Password") */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to show error styling */
  error?: boolean;
  /** Whether the input should be focused on mount */
  autoFocus?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * PasswordField Molecule
 *
 * A password input field with visibility toggle:
 * - Eye icon to show/hide password
 * - 322px width, 40px height
 * - Border styling matching design system
 *
 * @example
 * ```tsx
 * <PasswordField
 *   value={password}
 *   onChange={(e) => setPassword(e.target.value)}
 *   onKeyDown={handleKeyDown}
 * />
 * ```
 */
export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      placeholder = 'Password',
      disabled = false,
      error = false,
      autoFocus = false,
      className,
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);

    const toggleVisibility = React.useCallback(() => {
      setShowPassword((prev) => !prev);
    }, []);

    return (
      <div
        className={cn(
          'flex h-10 w-full items-center justify-between rounded border border-solid px-2',
          'transition-colors duration-200',
          error
            ? 'border-error'
            : 'border-grey-400 focus-within:border-dawn-black focus-within:bg-orange-100',
          className
        )}
      >
        <input
          ref={ref}
          type={showPassword ? 'text' : 'password'}
          autoFocus={autoFocus}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'flex-1 bg-transparent font-outfit text-[13px] font-normal leading-[1.3] text-dawn-black',
            'placeholder:text-grey-400 focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        />
        <button
          type="button"
          onClick={toggleVisibility}
          disabled={disabled}
          className={cn(
            'flex items-center justify-center w-4 h-4 text-grey-400',
            'hover:text-dawn-black transition-colors duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none'
          )}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    );
  }
);

PasswordField.displayName = 'PasswordField';

export default PasswordField;
